import { useCallback, useRef, useState } from 'react';
import { DirectUpload } from '@rails/activestorage';
import { apiClient, tokenStorage } from '../../../utils/api-client';

/** Rails' conventional direct-upload endpoint. */
const DIRECT_UPLOADS_PATH = '/rails/active_storage/direct_uploads';

/** Where the blob is made: on the API host, whatever page the app is on. */
function directUploadsUrl(): string {
  try {
    return new URL(DIRECT_UPLOADS_PATH, apiClient.getBaseURL() || window.location.origin).toString();
  } catch {
    return DIRECT_UPLOADS_PATH;
  }
}

/**
 * Sending the climb's photo to storage, as one call that resolves with the
 * signed id the create then carries.
 *
 * Straight over Active Storage's own `DirectUpload` rather than through the
 * React provider the rest of the app uploads with. The provider keeps an
 * `uploading` flag in state and its `handleUpload` returns without doing
 * anything while the flag is set — and a save that puts two files up one after
 * the other (the crop, then the whole shot it was cut from) asks for the second
 * before React has re-rendered the flag off, so the second never started and
 * the save waited for it forever. A `DirectUpload` is one object per file, with
 * nothing shared between two of them, and it reports every failure — checksum,
 * blob, storage — through the one callback, so a failed upload rejects rather
 * than hangs.
 */
export function usePhotoUpload() {
  const [uploading, setUploading] = useState(false);
  // Counted rather than flagged: two uploads may overlap, and the first to
  // finish must not report the other as done.
  const inFlight = useRef(0);

  const upload = useCallback((file: File) => {
    inFlight.current += 1;
    setUploading(true);

    return new Promise<string>((resolve, reject) => {
      const delegate = {
        // The blob is made on the API, which wants the token every other call
        // carries. Storage itself (the second request) is signed by the API and
        // takes nothing of ours.
        directUploadWillCreateBlobWithXHR(xhr: XMLHttpRequest) {
          xhr.setRequestHeader('Authorization', `Bearer ${tokenStorage.getToken() ?? ''}`);
        },
      };

      new DirectUpload(file, directUploadsUrl(), delegate).create((error, blob) => {
        // Typed as an Error, but what Active Storage actually hands back is a
        // string saying which request failed and with what status.
        if (error) return reject(error instanceof Error ? error : new Error(String(error)));
        if (!blob?.signed_id) return reject(new Error('The photo was not stored'));
        resolve(blob.signed_id);
      });
    }).finally(() => {
      inFlight.current -= 1;
      setUploading(inFlight.current > 0);
    });
  }, []);

  return { upload, uploading };
}
